/**
 * Copyright (c) 2019-2026 ForgeRock. All rights reserved.
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file for details.
 */

import getFQDN from '@forgerock/platform-shared/src/utils/getFQDN';
import { DEPLOYMENT_TYPES } from '@forgerock/platform-shared/src/constants/deploymentConstants';

const defaultState = {
  accountsEnabled: false,
  aiDevEnabled: false,
  amBaseURL: '',
  analyticsURL: '',
  aiAgentsEnabled: false,
  analyticsReportsAiAssistantEnabled: false,
  devIdmEnabled: false,
  idmWorkflowEnabled: false,
  autoCustomReportsEnabled: false,
  autoReportsEnabled: false,
  currentPackage: '',
  deploymentType: '',
  aicConnectionUisEnabled: false,
  fraasEnvironmentUrl: null,
  fraasFederationUrl: null,
  fraasLoggingKeyURL: null,
  fraasMonitoringURL: null,
  fraasPromotionAllowableIngressUrls: null,
  fraasPromotionUrl: null,
  fraasPromotionIngressUrl: null,
  fraasPromotionEgressUrl: null,
  googleFontsApiKey: '',
  googleMapsApiKey: '',
  governanceAgentsEnabled: false,
  governanceEnabled: false,
  governanceEnabledV4: false,
  governanceRoleMiningEnabled: false,
  hasAmUrl: false,
  helixEnvironmentId: '',
  helixEnvironmentUrl: '',
  idmBaseURL: '',
  idmOnly: false,
  igaApiUrl: null,
  igaOrchestrationApiUrl: null,
  isAirGapped: false,
  isFraas: false,
  journeyAIAgentVersion: null,
  journeyAIBuildAgentVersion: null,
  journeyAIEnabled: false,
  managedObjectMinimumUIFilterLength: {},
  maxIdleExpirationTime: null,
  maxSessionExpirationTime: null,
  newMultiselectEnabled: false,
  proxyConnectEnabled: false,
  mtlsEnabled: false,
  fraasMtlsFqdn: null,
  pingFederateUrl: null,
  promoteAppsViaApi: false,
  uiConfig: null,
  uiLocalizationEnabled: false,
  webStorageAvailable: true,
  workforceEnabled: false,
  wsfedEnabled: false,
  templateBuilderEnabled: false,
  enableSamlSigningCheckbox: false,
  enableSamlSkipMetadataSignatureVerification: false,
  enableMonitoringUI: false,
  devAppTemplatesEnabled: false,
};

const mutations = {
  setEnvironment(state, env) {
    const isFraas = env.VUE_APP_FRAAS?.toString() === 'true';
    const isAirGapped = env.VUE_APP_AIR_GAPPED?.toString() === 'true';

    if (isFraas) {
      state.isFraas = true;
      // VUE_APP_AIR_GAPPED is incompatible with VUE_APP_FRAAS — air-gapped deployments have no
      // internet egress and cannot reach FRaaS/IDCloud services. Warn at startup if misconfigured.
      if (isAirGapped) {
        // eslint-disable-next-line no-console
        console.warn('[Platform UI] VUE_APP_AIR_GAPPED and VUE_APP_FRAAS cannot both be true. Air-gapped mode is incompatible with FRaaS/IDCloud deployments.');
      }
    }
    if (isAirGapped) {
      state.isAirGapped = true;
    }
  },
  setBaseURLs(state, env) {
    if (env.VUE_APP_AM_URL?.length > 0) {
      state.amBaseURL = getFQDN(env.VUE_APP_AM_URL);
    }

    if (env.VUE_APP_IDM_URL) {
      state.idmBaseURL = getFQDN(env.VUE_APP_IDM_URL);
    }

    if (env.VUE_APP_ANALYTICS_API_URL) {
      state.analyticsURL = getFQDN(env.VUE_APP_ANALYTICS_API_URL);
    }

    if (env.VUE_APP_FRAAS?.toString() === 'true') {
      if (env.VUE_APP_FRAAS_ENV_URL) {
        state.fraasEnvironmentUrl = getFQDN(env.VUE_APP_FRAAS_ENV_URL);
      }

      if (env.VUE_APP_ENABLE_MTLS?.toString() === 'true') {
        state.fraasMtlsFqdn = env.VUE_APP_MTLS_FQDN;
      }

      if (env.VUE_APP_FRAAS_LOGGING_URL) {
        state.fraasLoggingKeyURL = getFQDN(env.VUE_APP_FRAAS_LOGGING_URL);
      }

      if (env.VUE_APP_FRAAS_MONITORING_URL) {
        state.fraasMonitoringURL = getFQDN(env.VUE_APP_FRAAS_MONITORING_URL);
      }

      if (env.VUE_APP_ENABLE_JOURNEY_AI?.toString() === 'true' || env.VUE_APP_ENABLE_AI_DEV?.toString() === 'true' || env.VUE_APP_ENABLE_ANALYTICS_REPORT_AI_ASSISTANT?.toString() === 'true') {
        state.helixEnvironmentUrl = getFQDN(env.VUE_APP_HELIX_ENVIRONMENT_URL);
        state.helixEnvironmentId = env.VUE_APP_HELIX_ENVIRONMENT_ID;
      }

      if (env.VUE_APP_ENABLE_JOURNEY_AI?.toString() === 'true' || env.VUE_APP_ENABLE_AI_DEV?.toString() === 'true') {
        state.journeyAIAgentVersion = env.VUE_APP_HELIX_JOURNEY_AGENT_VERSION;

        if (env.VUE_APP_ENABLE_AI_DEV?.toString() === 'true') {
          state.aiDevEnabled = true;
          state.journeyAIBuildAgentVersion = env.VUE_APP_HELIX_JOURNEY_BUILD_AGENT_VERSION;
        }
      }

      if (env.VUE_APP_ENABLE_GOVERNANCE?.toString() === 'true' || env.VUE_APP_ENABLE_GOVERNANCE_DEV?.toString() === 'true') {
        if (env.VUE_APP_IGA_API_URL) {
          state.igaApiUrl = getFQDN(env.VUE_APP_IGA_API_URL);
        }
        if (env.VUE_APP_IGA_ORCHESTRATION_API_URL) {
          state.igaOrchestrationApiUrl = getFQDN(env.VUE_APP_IGA_ORCHESTRATION_API_URL);
        }
      }

      if (env.VUE_APP_FRAAS_FEDERATION_ENFORCEMENT_URL) {
        state.fraasFederationUrl = getFQDN(env.VUE_APP_FRAAS_FEDERATION_ENFORCEMENT_URL);
      }

      if (env.VUE_APP_FRAAS_PROMOTION_URL) {
        state.fraasPromotionUrl = getFQDN(env.VUE_APP_FRAAS_PROMOTION_URL);
      }

      if (env.VUE_APP_FRAAS_ALLOWABLE_CONFIG_PROMOTION_INGRESS_URLS) {
        state.fraasPromotionAllowableIngressUrls = env.VUE_APP_FRAAS_ALLOWABLE_CONFIG_PROMOTION_INGRESS_URLS;
      }

      if (env.VUE_APP_FRAAS_PROMOTION_CONFIG_INGRESS_URL) {
        state.fraasPromotionIngressUrl = env.VUE_APP_FRAAS_PROMOTION_CONFIG_INGRESS_URL;
      }

      if (env.VUE_APP_FRAAS_PROMOTION_CONFIG_EGRESS_URL) {
        state.fraasPromotionEgressUrl = env.VUE_APP_FRAAS_PROMOTION_CONFIG_EGRESS_URL;
      }

      if (env.VUE_APP_ENABLE_WSFED?.toString() === 'true') {
        state.pingFederateUrl = getFQDN(env.VUE_APP_PINGFEDERATE_URL);
      }
    }

    if (env.VUE_APP_AUTO_ACCESS_REPORTS_URL) {
      state.autoAccessReportsUrl = getFQDN(env.VUE_APP_AUTO_ACCESS_REPORTS_URL);
    }

    if (env.VUE_APP_GOOGLE_FONTS_API_KEY) {
      state.googleFontsApiKey = env.VUE_APP_GOOGLE_FONTS_API_KEY;
    }

    if (env.VUE_APP_GOOGLE_MAPS_API_KEY) {
      state.googleMapsApiKey = env.VUE_APP_GOOGLE_MAPS_API_KEY;
    }
  },

  setUiConfig(state, newValue) {
    state.uiConfig = newValue;
  },

  setCurrentPackage(state, newValue) {
    state.currentPackage = newValue;
  },

  setFeatureFlags(state, env) {
    if (env.VUE_APP_AM_URL?.length > 0) {
      state.hasAmUrl = true;
    }

    if (env.VUE_APP_FRAAS === 'true') {
      if (env.VUE_APP_ENABLE_GOVERNANCE_DEV?.toString() === 'true') {
        state.governanceEnabled = true;
        state.governanceDevEnabled = true;
      } else if (env.VUE_APP_ENABLE_GOVERNANCE?.toString() === 'true') {
        state.governanceEnabled = true;
      }

      // Enable sub components of governance based on additional flags
      if (state.governanceEnabled) {
        if (env.VUE_APP_ENABLE_GOVERNANCE_ROLE_MINING?.toString() === 'true') {
          state.governanceRoleMiningEnabled = true;
        }
        if (env.VUE_APP_ENABLE_GOVERNANCE_AGENTS?.toString() === 'true') {
          state.governanceAgentsEnabled = true;
        }
      }

      if (env.VUE_APP_ENABLE_PROXY_CONNECT?.toString() === 'true') {
        state.proxyConnectEnabled = true;
      }

      if (env.VUE_APP_ENABLE_MTLS?.toString() === 'true') {
        state.mtlsEnabled = true;
      }

      if (env.VUE_APP_ENABLE_JOURNEY_AI?.toString() === 'true' || env.VUE_APP_ENABLE_AI_DEV?.toString() === 'true') {
        state.journeyAIEnabled = true;
      }

      if (env.VUE_APP_ENABLE_AIC_CONNECTION_UIS?.toString() === 'true') {
        state.aicConnectionUisEnabled = true;
      }

      if (env.VUE_APP_ENABLE_MONITORING_UI?.toString() === 'true') {
        state.enableMonitoringUI = true;
      }

      if (env.VUE_APP_PROMOTE_APPS_VIA_API?.toString() === 'true') {
        state.promoteAppsViaApi = true;
      }
    }

    if (env.VUE_APP_ENABLE_SAML_SIGNING_CHECKBOX?.toString() === 'true') {
      state.enableSamlSigningCheckbox = true;
    }

    if (env.VUE_APP_ENABLE_SAML_SKIP_METADATA_SIGNATURE_VERIFICATION?.toString() === 'true') {
      state.enableSamlSkipMetadataSignatureVerification = true;
    }

    if (env.VUE_APP_ENABLE_TEMPLATE_BUILDER?.toString() === 'true') {
      state.workforceEnabled = true;
      state.templateBuilderEnabled = true;
    } else if (env.VUE_APP_ENABLE_WORKFORCE?.toString() === 'true') {
      state.workforceEnabled = true;
    }

    if (env.VUE_APP_ENABLE_WSFED?.toString() === 'true') {
      state.wsfedEnabled = true;
    }

    if (env.VUE_APP_ENABLE_ANALYTICS_CUSTOM_REPORTING?.toString() === 'true') {
      state.autoCustomReportsEnabled = true;
    }

    if (env.VUE_APP_ENABLE_ANALYTICS_REPORTING?.toString() === 'true') {
      state.autoReportsEnabled = true;
    }

    if (env.VUE_APP_ENABLE_AI_AGENTS?.toString() === 'true' || (env.VUE_APP_FRAAS?.toString() === 'true' && !env.VUE_APP_FRAAS_PROMOTION_CONFIG_INGRESS_URL)) {
      state.aiAgentsEnabled = true;
    }

    if (env.VUE_APP_ENABLE_UI_LOCALIZATION?.toString() === 'true') {
      state.uiLocalizationEnabled = true;
    }

    if (env.VUE_APP_ENABLE_ANALYTICS_REPORT_AI_ASSISTANT?.toString() === 'true') {
      state.analyticsReportsAiAssistantEnabled = true;
    }

    if (env.VUE_APP_ENABLE_DEV_APP_TEMPLATES?.toString() === 'true') {
      state.devAppTemplatesEnabled = true;
    }

    if (env.VUE_APP_ENABLE_ACCOUNTS?.toString() === 'true') {
      state.accountsEnabled = true;
    }

    if (env.VUE_APP_ENABLE_DEV_IDM?.toString() === 'true') {
      state.devIdmEnabled = true;
    }
  },
  // Needed for Login since setFeatureFlags isn't called there
  setNewMultiselectEnabled(state, env) {
    state.newMultiselectEnabled = env.VUE_APP_ENABLE_NEW_MULTISELECT?.toString() === 'true';
  },
  setIdmWorkflowEnabled(state, val) {
    state.idmWorkflowEnabled = val;
  },
  setWebStorageAvailable(state, val) {
    state.webStorageAvailable = val;
  },
  setManagedObjectMinimumUIFilterLength(state, { managedObjectName, val }) {
    state.managedObjectMinimumUIFilterLength[managedObjectName] = val;
  },
  setMaxIdleExpirationTime(state, val) {
    state.maxIdleExpirationTime = val;
  },
  setMaxSessionExpirationTime(state, val) {
    state.maxSessionExpirationTime = val;
  },
  setDeploymentType(state, deploymentType) {
    state.deploymentType = deploymentType;
    if (deploymentType === DEPLOYMENT_TYPES.IDM) {
      state.idmOnly = true;
      state.workforceEnabled = false;
    }
  },
};

const getters = {
  maxIdleExpirationTime: (state) => state.maxIdleExpirationTime,
  maxSessionExpirationTime: (state) => state.maxSessionExpirationTime,
};

export default {
  state: defaultState,
  mutations,
  getters,
};
